import {remCalc} from '_themeUtils'

const productStickyAddToCart = {
    ProductStickyAddToCart: {
        baseStyle: ({isOpen}) => ({
            popover: {
                // Full-width bottom sheet. Positioning is owned by the Popper wrapper (see the
                // `pdpStickyMobile` popper slot in theme/components/popover.ts), which is pinned to the
                // viewport (position:fixed, inset:auto 0 0 0, transform:none, width:100%). This content box
                // just fills that wrapper edge-to-edge; Chakra defaults it to position:relative, so it does
                // NOT need its own fixed/left/right — only full width.
                w: '100%',
                maxW: '100%',
                // Side padding includes the safe-area insets so the content (text/buttons) stays out of the
                // notch / home-indicator area even though the sheet background spans edge-to-edge.
                padding: `30px calc(24px + env(safe-area-inset-right, 0px)) calc(30px + env(safe-area-inset-bottom, 0px)) calc(24px + env(safe-area-inset-left, 0px))`,
                borderRadius: '20px 20px 0 0',
                display: isOpen ? 'block' : 'none',
            },
            wrapper: {
                alignItems: 'flex-start',
                gap: 'spacing.3',
            },
            nameAndPriceContainer: {
                w: '100%',
                alignItems: 'flex-start',
                gap: remCalc(10),
            },
            productName: {
                textStyle: 'paragraphSm',
                fontWeight: 'normal',
            },
            productPrice: {
                textStyle: 'paragraphMd',
                fontWeight: 'light',
            },
            colorAndSizeContainer: {
                w: '100%',
                gap: remCalc(20),
            },
            variationAttributeContainer: {
                w: '100%',
                justifyContent: 'space-between',
            },
            variationAttributeText: {
                textStyle: 'paragraphSm',
                fontWeight: 'normal',
            },
            buyButtonsContainer: {
                w: '100%',
                mt: remCalc(38),
                // Uniform vertical spacing between all three buttons (add-to-cart / PayPal / Apple Pay),
                // matching the Figma layout (10px between each). Without it the express buttons render
                // flush on Safari, where both are present.
                gap: remCalc(10),
            },
            // Add-to-cart button height locked to 42px so all three buttons match: the PayPal SDK
            // (paypal_button_style height:42) and Apple Pay (ApplePayWrapper style height:42px) both
            // render at 42px, so this keeps the vertical gaps visually uniform.
            addToCartButton: {
                w: '100%',
                h: remCalc(42),
                minH: remCalc(42),
            },
            fastCheckoutWrapper: {
                w: '100%',
                // Fixed 42px box matching the express buttons' own height (PayPal iframe: 42,
                // Apple Pay: 42). This keeps all three buttons the same size with uniform gaps and
                // gives the PayPal iframe a stable box to mount into (prevents the Safari zero-height collapse).
                h: remCalc(42),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // PayPal renders an intermediate `.paypal_container` div that is 49px tall (its own
                // intrinsic height) while the iframe inside is 42px. Left unconstrained it overflows this
                // 42px box and makes the add-to-cart↔PayPal gap larger than the PayPal↔Apple Pay gap.
                // Constrain the container to the box height (NOT overflow:hidden, which would clip the
                // iframe) and center the 42px iframe within it, so all three gaps stay uniform.
                // Scoped to the PDP wrapper so the shared global `.paypal_container` (minicart) is untouched.
                '.paypal_container': {
                    h: remCalc(42),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            },
            // Tap-outside overlay for the confirmation layer. Sits below the popover content
            // (popover zIndex is 150000) but above the page, so a tap outside closes the layer
            // while a tap on the PayPal iframe (which renders above this overlay) does not.
            overlay: {
                position: 'fixed',
                // Inset-based full-screen cover instead of `100vw`/`100vh`. On iOS Safari with
                // `viewport-fit=cover`, `100vw` includes the side safe-area insets and overflows the
                // visible viewport (horizontal scroll on the PDP). Pinning all four edges to 0 covers
                // the viewport exactly without exceeding it.
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 149999,
            },
            sizeSelectorPopover: {
                w: {base: '100%', md: remCalc(500)},
                bottom: {base: 0, md: remCalc(30)},
                rigth: {base: 0, md: remCalc(30)},
                p: 'spacing.4',
                maxH: '70vh',
                overflow: 'auto',
                border: '1px solid',
                borderRadius: remCalc(8),
            },
            // Shared base for the mobile "BUY" CTA. The concrete position mode (inflow / fixed / hidden)
            // is merged on top of this by the component depending on scroll state:
            //   - inflow: the initial state, in the flow between name/price and the color section, so it
            //             never overlaps the color/size rows and keeps name+price above it.
            //   - fixed:  once the user scrolls past that natural position, the CTA anchors to the bottom
            //             of the viewport and stays visible for the rest of the page (including over the
            //             recommendations), clear of the iOS 26 translucent nav bar via safe-area.
            //   - hidden: when the footer comes into view, the CTA is hidden so it never covers it.
            buyButtonSticky: {
                w: '100%',
                // Small gap between the price and the BUY button. Kept tight so on some Chrome/Android
                // devices the in-flow button isn't pushed low enough to end up clipped under the browser
                // navigation bar.
                marginTop: 'spacing.2',
                textTransform: 'uppercase',
                letterSpacing: remCalc(4),
                zIndex: 'popover',
            },
            // Initial state: in the normal flow, right above the color section. Force full width and clear
            // the primary variant's `minW: 176px`, otherwise the button (wrapped by the inline
            // PopoverTrigger) collapses to 176px instead of spanning the content width.
            buyButtonInflow: {
                position: 'relative',
                w: '100%',
                minW: 'unset',
            },
            // Scrolled state: pinned to the bottom of the viewport, full content width (detailsContainer
            // provides the 24px side padding, but a fixed element leaves the flow, so re-create the same
            // 24px insets here).
            buyButtonFixed: {
                position: 'fixed',
                left: remCalc(24),
                right: remCalc(24),
                w: 'auto',
                minW: 'unset',
                // No top margin when pinned: the base `marginTop` only spaces the button from the price in
                // the in-flow state. When fixed, position is set by `bottom` alone, and dropping the extra
                // margin gives more clearance so the CTA isn't clipped by the browser nav bar on some devices.
                marginTop: 0,
                bottom: `calc(${remCalc(30)} + env(safe-area-inset-bottom, 0px))`,
            },
            // Hidden near the footer so the CTA never covers it.
            buyButtonHidden: {
                display: 'none',
            },
            buyButtonWrapper: {
                pl: 'spacing.6',
                pr: 'spacing.6',
            },
        }),
    },
}

export default productStickyAddToCart
